import { useState, useEffect, useRef } from 'react'
import MiddleEllipsis from '../MiddleEllipsis'
import { useJobDocuments, type JobDocument } from '../../hooks/useJobDocuments'

interface DocumentsTabProps {
  jobId: string
  readOnly?: boolean
}

export default function DocumentsTab({ jobId, readOnly = false }: DocumentsTabProps) {
  const { fetchJobDocuments, uploadDocument, deleteDocument, parseDocumentWithAI, uploading, parsing, error } =
    useJobDocuments()

  const [documents, setDocuments] = useState<JobDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [dragActive, setDragActive] = useState(false)
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const docTypeColors: Record<string, string> = {
    MBL: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200',
    HBL: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
    INVOICE: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
    OTHER: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
  }

  useEffect(() => {
    loadDocuments()
  }, [jobId])

  const loadDocuments = async () => {
    try {
      setLoading(true)
      const docs = await fetchJobDocuments(jobId)
      setDocuments(docs)
    } finally {
      setLoading(false)
    }
  }

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown'
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      handleFiles(files)
    }
  }

  const handleFiles = async (fileList: FileList) => {
    const filesArray = Array.from(fileList)

    for (const file of filesArray) {
      // Determine doc type from filename or default to OTHER
      let docType: 'MBL' | 'HBL' | 'INVOICE' | 'OTHER' = 'OTHER'
      const fileName = file.name.toUpperCase()
      if (fileName.includes('MBL')) docType = 'MBL'
      else if (fileName.includes('HBL')) docType = 'HBL'
      else if (fileName.includes('INVOICE')) docType = 'INVOICE'

      const doc = await uploadDocument(jobId, file, docType)
      if (doc) {
        setDocuments((prev) => [doc, ...prev])
      }
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files)
    }
  }

  const handleDeleteDoc = async (docId: string, filePath: string) => {
    if (!confirm('Are you sure you want to delete this document?')) {
      return
    }

    const success = await deleteDocument(docId, filePath)
    if (success) {
      setDocuments((prev) => prev.filter((d) => d.id !== docId))
      setSelectedDocId(null)
    }
  }

  const handleParseDoc = async (doc: JobDocument) => {
    const result = await parseDocumentWithAI(doc.id, doc.file_url)
    if (result) {
      // Update document in list
      setDocuments((prev) =>
        prev.map((d) =>
          d.id === doc.id
            ? {
                ...d,
                ai_parsed: true,
                ai_result: result.data,
                ai_confidence: result.confidence
              }
            : d
        )
      )
    }
  }

  const getFilePath = (doc: JobDocument): string => {
    // Extract the path from the file URL if it's a Supabase URL
    const url = new URL(doc.file_url)
    const pathParts = url.pathname.split('/storage/v1/object/public/job-documents/')
    return pathParts[1] || doc.file_name
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Upload Area */}
      {!readOnly && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Upload Documents
          </label>

          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition ${
              dragActive
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,application/pdf"
              onChange={handleFileInput}
              className="hidden"
              disabled={uploading}
            />

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="mx-auto mb-3"
            >
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </button>

            <div>
              <p className="text-gray-700 dark:text-gray-300 font-medium">
                {uploading ? 'Uploading...' : 'Drop PDF files here or click to upload'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Supports MBL, HBL, Invoice, and other documents (PDF only, max 50MB)
              </p>
            </div>

            {uploading && (
              <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-50 rounded-lg flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Documents List */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Uploaded Documents ({documents.length})
        </h3>

        {documents.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-gray-500 dark:text-gray-400">No documents uploaded yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className={`bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4 transition cursor-pointer ${
                  selectedDocId === doc.id ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => setSelectedDocId(selectedDocId === doc.id ? null : doc.id)}
              >
                {/* Document Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1">
                    {/* File Icon */}
                    <div className="flex-shrink-0">
                      <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.707 7.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l2-2a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" />
                        <path d="M5 2a2 2 0 012-2h6a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V2z" />
                      </svg>
                    </div>

                    {/* Document Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${docTypeColors[doc.doc_type]}`}>
                          {doc.doc_type}
                        </span>
                        {doc.ai_parsed && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                            ✓ AI Parsed
                          </span>
                        )}
                      </div>
                      <MiddleEllipsis text={doc.file_name} className="font-medium text-gray-900 dark:text-white mt-1" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatFileSize(doc.file_size)} • Uploaded{' '}
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0 flex gap-2 ml-2">
                    {!readOnly && !doc.ai_parsed && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleParseDoc(doc)
                        }}
                        disabled={parsing}
                        title="Parse document with Claude AI"
                        className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition"
                      >
                        {parsing ? (
                          <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        )}
                      </button>
                    )}

                    {!readOnly && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteDoc(doc.id, getFilePath(doc))
                        }}
                        title="Delete document"
                        className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}

                    <a
                      href={doc.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition"
                      title="Open document"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>

                {/* AI Parsed Data */}
                {doc.ai_parsed && doc.ai_result && selectedDocId === doc.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900 dark:text-white">Parsed Data</h4>
                      {doc.ai_confidence && (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Confidence: {Math.round(doc.ai_confidence * 100)}%
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {Object.entries(doc.ai_result).map(([key, value]) => (
                        <div key={key}>
                          <dt className="text-gray-600 dark:text-gray-400 capitalize">
                            {key.replace(/_/g, ' ')}:
                          </dt>
                          <dd className="text-gray-900 dark:text-white font-medium">
                            {Array.isArray(value)
                              ? value.join(', ')
                              : typeof value === 'object'
                                ? JSON.stringify(value)
                                : String(value)}
                          </dd>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-300">
          💡 <span className="font-medium">Tip:</span> Upload MBL and HBL PDFs to automatically extract shipping information. 
          AI parsing is available for supported document formats.
        </p>
      </div>
    </div>
  )
}
